import type { FC, ReactElement, ReactNode } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { ErrorMessage, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import type { FieldMetaProps } from 'formik'
import CloseCircleIcon from 'mdi-react/CloseCircleIcon'
import { twJoin, twMerge } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

export const TimelineItem: FC<{
  icon?: ReactElement
  title: string
  description?: string[]
  meta?: FieldMetaProps<any>
  wrapperClassName?: string
  clearBtnOptions?: {
    hasButton?: boolean
    disabled?: boolean
    onBtnClick?: () => void
  }
  checkboxComponent?: ReactNode
  children?: ReactNode
  isActive?: boolean
  isRightError?: boolean
}> = ({
  icon,
  title,
  description,
  meta,
  wrapperClassName,
  clearBtnOptions,
  checkboxComponent,
  children,
  isActive,
  isRightError = false,
}) => {
  const translate = useTranslation()
  const storedWhiteLabel = localStorage.getItem('org') as WhiteLabel
  const whiteLabel = storedWhiteLabel || 'control'

  return (
    <div
      className={twMerge(
        'group relative flex flex-col items-start justify-between gap-4 py-4 pl-8 sm:flex-row sm:gap-0 sm:py-6 sm:pl-11',
        wrapperClassName
      )}
    >
      <div>
        <div
          className={twJoin(
            'mobile:flex-row mb-1 flex flex-col group-last:before:hidden',
            'before:absolute before:left-4 before:h-full before:self-start before:bg-gray-300 before:px-px before:content-[""] sm:before:left-0 sm:before:ml-6',
            'before:-translate-x-3 before:translate-y-1'
          )}
        >
          {icon && (
            <span
              className={twMerge(
                'bg-primary absolute left-4 flex h-7 w-7 -translate-x-6 translate-y-0.5 items-center justify-center rounded-full text-white ring-8 ring-white sm:left-0 sm:ml-6',
                whiteLabelColorClass[whiteLabel]?.icon,
                !isActive && 'bg-[#ccc]'
              )}
            >
              {icon}
            </span>
          )}
          <div className="text-md flex items-center gap-2 font-bold text-black">
            <span>{title}</span>
            {clearBtnOptions?.hasButton && (
              <>
                <Tooltip id="clear-reminder" size="large" className="z-[100]" />
                <Button
                  variant="tertiary"
                  data-tooltip-id="clear-reminder"
                  data-tooltip-content={translate('Clear reminder')}
                  disabled={clearBtnOptions.disabled}
                  className={twJoin(
                    'm-0 min-h-fit justify-center bg-transparent p-0 leading-none text-gray-500 focus:text-gray-500 active:text-gray-500 disabled:pointer-events-none disabled:bg-transparent disabled:text-zinc-400',
                    whiteLabelColorClass[whiteLabel]?.clearBtn ||
                      whiteLabelColorClass.skytech?.clearBtn
                  )}
                  Icon={<CloseCircleIcon className="m-0 items-start" />}
                  onClick={clearBtnOptions.onBtnClick}
                />
              </>
            )}
          </div>
        </div>

        {description &&
          description.length > 0 &&
          description.map((desc, index) => (
            <div key={index} className="text-sm text-gray-500">
              {desc}
            </div>
          ))}
        {checkboxComponent && <div>{checkboxComponent}</div>}
        {!isRightError && meta?.error && meta.touched && <ErrorMessage>{meta.error}</ErrorMessage>}
      </div>

      {children && (
        <div className="basis-1/3">
          <div className="flex items-center justify-end">
            {children}
            {isRightError && meta?.error && meta.touched && (
              <ErrorMessage className="shadow-basic absolute right-10 top-[4.5rem] z-10 m-0 min-w-24 max-w-96 rounded-md bg-white p-2">
                {meta.error}
              </ErrorMessage>
            )}
            <Translation id="days" className="ml-2 text-base text-gray-500" />
          </div>
        </div>
      )}
    </div>
  )
}

const whiteLabelColorClass: Partial<Record<WhiteLabel, Record<'icon' | 'clearBtn', string>>> = {
  control: {
    icon: 'bg-skytech-orange text-white',
    clearBtn: 'hover:text-skytech-orange',
  },
  skytech: {
    icon: 'bg-skytech-orange text-white',
    clearBtn: 'hover:text-skytech-orange',
  },
  tcm: {
    icon: 'bg-skytech-orange text-white',
    clearBtn: 'hover:text-skytech-orange',
  },
  arp: {
    icon: 'bg-arp text-white',
    clearBtn: 'hover:text-arp',
  },
  mobit: {
    icon: 'bg-mobit text-white',
    clearBtn: 'hover:text-mobit',
  },
  evry: {
    icon: 'text-white bg-tietoevry',
    clearBtn: 'hover:text-tietoevry',
  },
  tietoevry: {
    icon: 'text-white bg-tietoevry',
    clearBtn: 'hover:text-tietoevry',
  },
  bechtle: {
    icon: 'bg-bechtle text-white',
    clearBtn: 'hover:text-bechtle',
  },
  csi: {
    icon: 'bg-csi-maroon text-white',
    clearBtn: 'hover:text-csi-maroon',
  },
  telia: {
    icon: 'bg-telia text-white',
    clearBtn: 'hover:text-telia',
  },
  elkjop: {
    icon: 'bg-elkjop text-white',
    clearBtn: 'hover:text-elkjop',
  },
  threeStepIT: {
    icon: 'bg-threeStepIT text-white',
    clearBtn: 'hover:text-threeStepIT',
  },
  komplett: {
    icon: 'bg-komplett text-white',
    clearBtn: 'hover:text-komplett',
  },
  ikomm: {
    icon: 'bg-skytech-orange text-white',
    clearBtn: 'hover:text-skytech-orange',
  },
}
